import type { Aabb2, Point2 } from './point';

/** True when `inner` lies entirely within `outer` (touching counts as inside). */
export const aabbContainsAabb = (outer: Aabb2, inner: Aabb2): boolean =>
  inner[0] >= outer[0] &&
  inner[1] >= outer[1] &&
  inner[2] <= outer[2] &&
  inner[3] <= outer[3];

/**
 * Grows a box by `margin` on every side. A negative margin shrinks it, and may
 * invert it — callers wanting a floor should clamp.
 */
export const inflateAabb = (bounds: Aabb2, margin: number): Aabb2 => [
  bounds[0] - margin,
  bounds[1] - margin,
  bounds[2] + margin,
  bounds[3] + margin,
];

/** True when the two boxes share any area or edge. */
export const aabbIntersectsAabb = (a: Aabb2, b: Aabb2): boolean =>
  a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];

export const aabbContainsPoint = (bounds: Aabb2, [x, y]: Point2): boolean =>
  x >= bounds[0] && x <= bounds[2] && y >= bounds[1] && y <= bounds[3];

/**
 * True when a segment touches a box at all — crossing it, or lying inside it.
 *
 * Liang–Barsky parametric clipping rather than testing the four edges in turn: it
 * is branch-light, handles the fully-inside case for free, and is exact for
 * axis-aligned boxes.
 */
export const segmentIntersectsAabb = (
  a: Point2,
  b: Point2,
  bounds: Aabb2,
): boolean => {
  const [ax, ay] = a;
  const [bx, by] = b;
  const [minX, minY, maxX, maxY] = bounds;

  const dx = bx - ax;
  const dy = by - ay;

  let tMin = 0;
  let tMax = 1;

  // One slab per axis; each clips the parameter range the segment can occupy.
  const clip = (
    direction: number,
    origin: number,
    low: number,
    high: number,
  ) => {
    if (direction === 0) {
      // Parallel to this slab: it either misses entirely or is unconstrained by it.
      return origin >= low && origin <= high;
    }
    const tEnter = (low - origin) / direction;
    const tExit = (high - origin) / direction;
    const near = Math.min(tEnter, tExit);
    const far = Math.max(tEnter, tExit);
    if (near > tMin) tMin = near;
    if (far < tMax) tMax = far;
    return tMin <= tMax;
  };

  return clip(dx, ax, minX, maxX) && clip(dy, ay, minY, maxY);
};

/** True when any part of the polyline touches the box. */
export const polylineIntersectsAabb = (
  points: readonly Point2[],
  bounds: Aabb2,
): boolean => {
  if (points.length === 1) {
    return aabbContainsPoint(bounds, points[0]);
  }
  for (let index = 1; index < points.length; index += 1) {
    if (segmentIntersectsAabb(points[index - 1], points[index], bounds)) {
      return true;
    }
  }
  return false;
};

/**
 * True when `point` lies inside the polygon the run encloses.
 *
 * Ray casting along +x with a half-open edge rule (`>` on one end, `<=` on the
 * other), which is what makes a ray that grazes a vertex count it once rather than
 * twice or not at all. The run is treated as implicitly closed.
 *
 * Points exactly on the boundary are not guaranteed either answer — that is
 * inherent to the test, and callers that care should use a distance check instead.
 */
export const polygonContainsPoint = (
  points: readonly Point2[],
  [x, y]: Point2,
): boolean => {
  if (points.length < 3) {
    return false;
  }

  let isInside = false;
  for (
    let index = 0, previous = points.length - 1;
    index < points.length;
    previous = index, index += 1
  ) {
    const [ix, iy] = points[index];
    const [px, py] = points[previous];

    const straddles = iy > y !== py > y;
    if (straddles && x < ((px - ix) * (y - iy)) / (py - iy) + ix) {
      isInside = !isInside;
    }
  }
  return isInside;
};
